import { Checkbox, Input, InputNumber } from 'rsuite';
import React, { Dispatch, SetStateAction } from 'react';
import { FormField, FormGrid } from '../../../common/FormModal';
import LanguageTabs, {
  getLanguagePanelId,
  getLanguageTabId,
  LanguageKey,
} from '../../../common/LanguageTabs';
import RichTextEditor from '../../../common/RichTextEditor';

type Props = {
  modalCourseCipher: string;
  modalCourseName: string;
  modalCourseNameEn: string;
  modalCourseNameDe: string;
  modalCourseDescription: string;
  modalCourseDescriptionEn: string;
  modalCourseDescriptionDe: string;
  modalCourseShortDescription: string;
  modalCourseShortDescriptionEn: string;
  modalCourseShortDescriptionDe: string;
  modalCourseActive: boolean;
  modalCourseAcceptsRequests: boolean;
  modalCoursePriority: number;
  setModalCourseCipher:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseName:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseNameEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseNameDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseDescription:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseDescriptionEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseDescriptionDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseShortDescription:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseShortDescriptionEn:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseShortDescriptionDe:
    | Dispatch<SetStateAction<string>>
    | ((newValue: any) => void);
  setModalCourseActive:
    | Dispatch<SetStateAction<boolean>>
    | ((newValue: any) => void);
  setModalCourseAcceptsRequests:
    | Dispatch<SetStateAction<boolean>>
    | ((newValue: any) => void);
  setModalCoursePriority:
    | Dispatch<SetStateAction<number>>
    | ((newValue: any) => void);
};

export default function CourseModalContent({
  modalCourseCipher,
  modalCourseName,
  modalCourseNameEn,
  modalCourseNameDe,
  modalCourseDescription,
  modalCourseDescriptionEn,
  modalCourseDescriptionDe,
  modalCourseShortDescription,
  modalCourseShortDescriptionEn,
  modalCourseShortDescriptionDe,
  modalCourseActive,
  modalCourseAcceptsRequests,
  modalCoursePriority,
  setModalCourseCipher,
  setModalCourseName,
  setModalCourseNameEn,
  setModalCourseNameDe,
  setModalCourseDescription,
  setModalCourseDescriptionEn,
  setModalCourseDescriptionDe,
  setModalCourseShortDescription,
  setModalCourseShortDescriptionEn,
  setModalCourseShortDescriptionDe,
  setModalCourseActive,
  setModalCourseAcceptsRequests,
  setModalCoursePriority,
}: Props) {
  const [activeLanguage, setActiveLanguage] = React.useState<LanguageKey>('ua');
  const languageTabPrefix = 'course-language';

  const languageConfig: Record<
    LanguageKey,
    {
      name: string;
      setName: (value: string) => void;
      description: string;
      setDescription: (value: string) => void;
      shortDescription: string;
      setShortDescription: (value: string) => void;
    }
  > = {
    ua: {
      name: modalCourseName,
      setName: setModalCourseName,
      description: modalCourseDescription,
      setDescription: setModalCourseDescription,
      shortDescription: modalCourseShortDescription,
      setShortDescription: setModalCourseShortDescription,
    },
    en: {
      name: modalCourseNameEn,
      setName: setModalCourseNameEn,
      description: modalCourseDescriptionEn,
      setDescription: setModalCourseDescriptionEn,
      shortDescription: modalCourseShortDescriptionEn,
      setShortDescription: setModalCourseShortDescriptionEn,
    },
    de: {
      name: modalCourseNameDe,
      setName: setModalCourseNameDe,
      description: modalCourseDescriptionDe,
      setDescription: setModalCourseDescriptionDe,
      shortDescription: modalCourseShortDescriptionDe,
      setShortDescription: setModalCourseShortDescriptionDe,
    },
  };

  const activeLanguageConfig = languageConfig[activeLanguage];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Checkbox
          checked={modalCourseActive}
          onChange={(_value, checked) => {
            setModalCourseActive(checked);
          }}
        >
          Відображати на сайті
        </Checkbox>
        <Checkbox
          checked={modalCourseAcceptsRequests}
          onChange={(_value, checked) => {
            setModalCourseAcceptsRequests(checked);
          }}
        >
          Відкрити запис на курс
        </Checkbox>
      </div>
      <div style={{ marginTop: 16 }}>
        <FormGrid>
          <FormField label="Шифр" hint="Для кодування назви групи">
            <Input
              placeholder="RH"
              value={modalCourseCipher || ''}
              onChange={setModalCourseCipher}
            />
          </FormField>
          <FormField label="Пріоритет">
            <InputNumber
              placeholder="0"
              value={modalCoursePriority ?? 0}
              onChange={value => setModalCoursePriority(Number(value) || 0)}
              min={0}
            />
          </FormField>
        </FormGrid>
      </div>
      <LanguageTabs
        activeLanguage={activeLanguage}
        onChange={setActiveLanguage}
        idPrefix={languageTabPrefix}
      />
      <div
        className="language-panel"
        role="tabpanel"
        id={getLanguagePanelId(languageTabPrefix, activeLanguage)}
        aria-labelledby={getLanguageTabId(languageTabPrefix, activeLanguage)}
      >
        <div className="language-fields">
          <FormField label="Назва">
            <Input
              placeholder="Назва"
              value={activeLanguageConfig.name || ''}
              onChange={activeLanguageConfig.setName}
            />
          </FormField>
          <FormField label="Короткий опис" maxLength={1000}>
            <Input
              as="textarea"
              rows={3}
              placeholder="Короткий опис"
              value={activeLanguageConfig.shortDescription || ''}
              onChange={activeLanguageConfig.setShortDescription}
            />
          </FormField>
          <FormField label="Опис">
            <RichTextEditor
              placeholder="Опис"
              value={activeLanguageConfig.description || ''}
              onChange={activeLanguageConfig.setDescription}
            />
          </FormField>
        </div>
      </div>
    </>
  );
}
